import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { saveSection } from 'components/section-edit/saveSection';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetContractByIdQuery, useUpdateContractMutation } from 'store/api/contracts';
import { ClassifierData } from 'types/classifiers';
import { Contract, ContractForm } from 'types/contracts';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { useClassifiers } from 'utils/useClassifiers';
import {
    CONTRACT_FORM_CLASSIFIERS,
    ContractSectionId,
    contractDtoToForm,
    contractFormToRequest,
    contractSectionFields,
    widensToPricing,
} from '../contractFormMapping';
import {
    BeneficialOwnersFields,
    BillingFields,
    DatesFields,
    FinancialFields,
    GeneralFields,
    NotesFields,
} from './ContractFields';
import { ContractFormState, useContractForm } from './useContractForm';

// The form shows its Financial section, and with it the terms and the bank account, for these types only.
const hasFinancialSection = (form: ContractFormState) =>
    !form.isAmlOrUbo && !form.isRepresentation && !form.isCooperation;

const SECTION_FIELDS: Record<ContractSectionId, (form: ContractFormState) => ReactNode> = {
    // A type that needs pricing on a contract stored without it brings the pricing in, so the save is valid.
    contract: (form) => (
        <>
            <GeneralFields form={form} part="contract" />
            {hasFinancialSection(form) ? <FinancialFields form={form} part="terms" /> : null}
            {form.initialData && widensToPricing(form.contractType?.value, form.initialData) ? (
                <FinancialFields form={form} part="pricing" />
            ) : null}
        </>
    ),
    parties: (form) => (
        <>
            <GeneralFields form={form} part="parties" />
            {hasFinancialSection(form) ? <FinancialFields form={form} part="bank-account" /> : null}
        </>
    ),
    'beneficial-owners': (form) => <BeneficialOwnersFields form={form} />,
    billing: (form) => <BillingFields form={form} />,
    dates: (form) => <DatesFields form={form} />,
    notes: (form) => <NotesFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields
            control={form.methods.control as unknown as Control<FieldValues>}
            fields={form.filteredCustomFields}
        />
    ),
};

interface SectionFormProps {
    section: ContractSectionId;
    initialData: ContractForm;
    classifiers: Record<string, ClassifierData>;
    customFields: CustomField[];
    onClose: () => void;
}

function ContractSectionForm({ section, initialData, classifiers, customFields, onClose }: SectionFormProps) {
    const form = useContractForm({ type: 'edit', initialData, classifiers, entityCustomFields: customFields });
    // The page's own query argument, so the refetch refreshes the record it shows.
    const { id: contractId = '' } = useParams();
    const { refetch } = useGetContractByIdQuery(contractId);
    const [updateContract] = useUpdateContractMutation();

    const onSave = (values: ContractForm) =>
        saveSection(values, {
            section,
            sectionFields: contractSectionFields(values, initialData),
            fetchLatest: () => refetch().unwrap(),
            toForm: (dto: Contract) =>
                contractDtoToForm(dto, { classifiers, customFieldValues: form.customFieldValuesOf(dto.customFields) }),
            // A service agreement stored with no increment, which the server refuses once increment control is on,
            // takes the one this card offered (see `offersIncrement`).
            toRequest: (edited) => {
                const merged = { ...edited, timecardIncrement: edited.timecardIncrement ?? values.timecardIncrement };
                return contractFormToRequest(
                    merged,
                    form.mapCustomFieldsToDto(merged as unknown as Record<string, unknown>)
                );
            },
            update: (request) => updateContract({ ...request, id: contractId }).unwrap(),
            onSaved: onClose,
        });

    // The Contract card edits the increment itself; any other card asks for it while the contract stores none, so
    // its save is valid. The form prefills the tenant default there, as the full form does.
    const offersIncrement = section !== 'contract' && initialData.timecardIncrement == null;

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
            {offersIncrement ? <FinancialFields form={form} part="increment" /> : null}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: ContractSectionId;
    contract: Contract;
    onClose: () => void;
}

function ContractSectionEditor({ section, contract, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: CONTRACT_FORM_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFields,
    } = useCustomFields({ entityType: EntityType.CONTRACT, fields: contract.customFields });

    const initialData = useFrozenOnceLoaded(!isLoadingClassifiers && !isLoadingCustomFields, () =>
        contractDtoToForm(contract, { classifiers, customFieldValues: initialCustomFieldsObject })
    );

    if (!initialData) {
        return <Loader />;
    }
    return (
        <ContractSectionForm
            section={section}
            initialData={initialData}
            classifiers={classifiers}
            customFields={customFields}
            onClose={onClose}
        />
    );
}

interface ContractSectionProps {
    id: ContractSectionId;
    titleId: string;
    contract: Contract;
    /** The read view, handed the section's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** This section's own gate, on top of the page's. */
    canEdit?: boolean;
    /** The read view's rows: those no field edits stay under the fields while the section edits. */
    rows?: Row[];
}

/** A contract section on the record page that edits in place with the contract form's fields for that section. */
export function ContractSection({ id, titleId, contract, children, canEdit, rows }: ContractSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            canEdit={canEdit}
            rows={rows}
            editor={(close) => <ContractSectionEditor section={id} contract={contract} onClose={close} />}
        >
            {children}
        </SectionEdit>
    );
}
